import React, { useState } from 'react';
import AdminLayout from '../AdminLayout';
import MagicButton from 'components/MagicButton';
import { useGetSeoReportQuery, useGenerateSeoReportMutation } from 'store/services/admin/adminSeoApi';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import { toast } from 'react-toastify';
import GapZoneTab from './GapZoneTab';
import CompetitorIntelTab from './CompetitorIntelTab';
import ContentPlanTab from './ContentPlanTab';
import DraftsTab from './DraftsTab';
import RunAnalysisButton from './RunAnalysisButton';
import './SeoReportPage.css';

type SeoTab = 'gap-zone' | 'competitor-intel' | 'content-plan' | 'drafts';

const TABS: { key: SeoTab; label: string }[] = [
  { key: 'gap-zone', label: 'Gap Zone' },
  { key: 'competitor-intel', label: 'Competitor Intel' },
  { key: 'content-plan', label: 'Content Plan' },
  { key: 'drafts', label: 'Drafts' },
];

const SeoReportPage: React.FC = () => {
  const { data, isLoading, error } = useGetSeoReportQuery();
  const [generateReport, { isLoading: isGenerating }] = useGenerateSeoReportMutation();
  const [activeTab, setActiveTab] = useState<SeoTab>('gap-zone');

  const isReport = data && !('status' in data);

  const handleGenerate = async () => {
    try {
      const result = await generateReport().unwrap();
      if (result && 'status' in result && (result as Record<string, unknown>).status === 'error') {
        toast.error((result as Record<string, unknown>).message as string || 'Failed to generate report');
        return;
      }
      toast.success('SEO report generated successfully');
    } catch (err: unknown) {
      const message = (err && typeof err === 'object' && 'data' in err)
        ? String((err as { data: { message?: string } }).data?.message || 'Failed to generate report')
        : 'Failed to generate report';
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Admin Panel - SEO Report" currentPath="/admin/seo">
        <div className="seo-report-page">
          <div className="seo-loading">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Admin Panel - SEO Report" currentPath="/admin/seo">
        <div className="seo-report-page">
          <div className="seo-error-state">
            <h3>Failed to load SEO report</h3>
            <p>Please try again later.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // No report exists yet — show empty state with generate button
  if (!isReport) {
    return (
      <AdminLayout title="Admin Panel - SEO Report" currentPath="/admin/seo">
        <div className="seo-report-page">
          <div className="seo-empty-state">
            <h3>No SEO report has been generated yet.</h3>
            <p>Click the button below to pull data from Google Search Console.</p>
            <MagicButton
              label={isGenerating ? 'Generating...' : 'Generate Report'}
              onPress={handleGenerate}
              color="purple"
              isLoading={isGenerating}
              isDisabled={isGenerating}
            />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Admin Panel - SEO Report" currentPath="/admin/seo">
      <div className="seo-report-page">
        {/* Header */}
        <div className="seo-header">
          <div>
            <h2 className="seo-title">SEO Report</h2>
            <p className="seo-subtitle">
              Gap zone keywords, competitor intelligence, and content planning
            </p>
            <span
              className="seo-last-updated"
              title={new Date(data.generatedAt).toLocaleString()}
            >
              Last updated: {formatRelativeTime(data.generatedAt)}
            </span>
          </div>
          <RunAnalysisButton />
        </div>

        {/* Tabs */}
        <div className="seo-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`seo-tab ${activeTab === tab.key ? 'seo-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'gap-zone' && <GapZoneTab data={data} />}
        {activeTab === 'competitor-intel' && <CompetitorIntelTab isActive={activeTab === 'competitor-intel'} />}
        {activeTab === 'content-plan' && <ContentPlanTab isActive={activeTab === 'content-plan'} />}
        {activeTab === 'drafts' && <DraftsTab isActive={activeTab === 'drafts'} />}
      </div>
    </AdminLayout>
  );
};

export default SeoReportPage;
